<script setup lang="ts">
import type { MaybeRefOrGetter } from 'vue';
import type { FieldRule, PerActionFieldRulesOrWildcard, PerActionItemRule, PolicyDocumentAction } from '#layers/access/types/permissions/policy';
import type { GlobalCollectionId, GlobalFieldId, GlobalPrimitiveFieldId } from '#layers/data-model/public';
import { computed, ref, toRef, toValue, watch } from 'vue';
import { usePolicyFieldRules } from '#layers/access/composables/policy/use-policy-field-rules';
import { FIELD_WILDCARD } from '#layers/access/constants/permissions';
import { CollectionFieldSelect, getCollectionFields } from '#layers/data-model/public';
import { useWorkspaceSchemaStore } from '#layers/workspace/public';
import PolicyFieldRuleFilterButton from './PolicyFieldRuleFilterButton.vue';

interface PolicyFieldRulesProps {
	collectionId: GlobalCollectionId;
	action: PolicyDocumentAction;
	itemRules: MaybeRefOrGetter<PerActionItemRule>;
}

const props = defineProps<PolicyFieldRulesProps>();
const modelValue = defineModel<PerActionFieldRulesOrWildcard>({ required: true });

const { findCollectionById, findFieldByName } = useWorkspaceSchemaStore();
const collection = computed(() => findCollectionById(props.collectionId));

const preSelectedFieldIds = computed<GlobalFieldId[]>(() => {
	if (!collection.value) return [];

	if (modelValue.value === FIELD_WILDCARD) {
		return getCollectionFields(collection.value!, { includeRelationFields: false }).map((field) => field.id as GlobalFieldId);
	}
	else {
		return Object.keys(modelValue.value).map((fieldName) => findFieldByName(collection.value!, fieldName)?.id as GlobalFieldId);
	}
});

const fieldRuleFieldIds = ref<GlobalFieldId[]>(preSelectedFieldIds.value!);
const fieldRuleFields = computed(() => {
	const fields = getCollectionFields(collection.value!, { includeRelationFields: false });
	return fields.filter((field) => fieldRuleFieldIds.value.includes(field.id as GlobalFieldId));
});

const fields = computed(() => {
	const collection = findCollectionById(props.collectionId);
	return getCollectionFields(collection!, { includeRelationFields: false });
});

const { hasFollowItemRules } = usePolicyFieldRules({ itemRules: toRef(props.itemRules), fieldRules: ref(modelValue), action: props.action });

const hasWildcardRef = ref(modelValue.value === FIELD_WILDCARD);
const hasWildcard = computed({
	get() {
		return hasWildcardRef.value;
	},
	set(newHasWildcard: boolean) {
		hasWildcardRef.value = newHasWildcard;

		if (newHasWildcard) {
			fieldRuleFieldIds.value = fields.value.map((f) => f.id ?? '');
		}
	},
});

watch([fieldRuleFields, fieldRuleFieldIds, hasWildcard], () => {
	if (hasWildcard.value) {
		modelValue.value = FIELD_WILDCARD;
	}
	else {
		modelValue.value = fieldRuleFields.value.reduce((acc, f) => {
			const currentFilter = (modelValue.value[f.apiName as keyof PerActionFieldRulesOrWildcard] as FieldRule)?.filter;
			acc[f.apiName] = props.action === 'create' ? {} : { filter: currentFilter ?? null };
			return acc;
		}, {} as Record<string, any>);
	}
});

function handleFollowItemRulesChange(fieldName: string, follow: boolean) {
	if (modelValue.value === FIELD_WILDCARD) return;

	modelValue.value = {
		...modelValue.value,
		[fieldName]: { filter: follow ? toValue(props.itemRules)?.filter : { _and: [] } } as FieldRule,
	};
}

const hasConfiguration = computed(() => {
	if (modelValue.value === FIELD_WILDCARD) return true;
	return Object.keys(modelValue.value).length > 0;
});
</script>

<template>
	<DFormSeparator
		:label="$t('policy.field_rules.title')"
		:description="$t('policy.field_rules.configuration_description', { action: $t(`policy.action.${action}`).toLowerCase() })"
		:badge="hasConfiguration ? {
			color: 'primary',
			variant: 'soft',
			label: $t('common.configuration_set'),
		} : {
			color: 'neutral',
			variant: 'soft',
			label: $t('common.configuration_not_set') }"
	/>

	<DSwitch
		v-model="hasWildcard"
		:label="$t('policy.field_rules.enable_wildcard')"
		:description="$t('policy.field_rules.enable_wildcard_description')"
	/>

	<DNotice
		v-if="hasWildcard"
		color="info"
		class="mb-4"
		:title="$t('policy.field_rules.wildcard_warning_title')"
		:description="$t('policy.field_rules.wildcard_warning_description')"
	/>

	<CollectionFieldSelect
		v-if="collection"
		:model-value="fieldRuleFieldIds as GlobalPrimitiveFieldId[]"
		:disabled="hasWildcard"
		:collection
		presentation-style="list"
		include-primitive-fields
		:include-relation-fields="false"
		include-constrained-fields
		multiple
		@update:model-value="fieldRuleFieldIds = $event"
	>
		<template #item="{ item }">
			<div class="flex h-7 flex-1 flex-row items-center gap-2">
				<div class="flex-1">
					{{ item.label }}
				</div>
				<PolicyFieldRuleFilterButton
					v-if="props.action !== 'create'"
					v-model="modelValue[item.apiName as keyof PerActionFieldRulesOrWildcard] as FieldRule"
					:follow-item-rules="hasFollowItemRules(item.apiName)"
					:collection-id="props.collectionId"
					:disabled="hasFollowItemRules(item.apiName)"
					:mode="hasFollowItemRules(item.apiName) ? 'follow-item-rules' : 'custom-field-rules'"
					@follow-item-rules-change="handleFollowItemRulesChange(item.apiName, $event)"
				/>
			</div>
		</template>
	</CollectionFieldSelect>
</template>
